/**
 * Read Only Context
 *
 * Manages the read-only state independently from the main MasterclassContext.
 * No database interactions, no persistence.
 */
import { createContext, useContext, useState, ReactNode, useMemo } from 'react';

import configJson from '@/config.json';
import { useAuthUser } from '@/hooks/queries';

import { ReadOnlyContextType } from '../types';

const ReadOnlyContext = createContext<ReadOnlyContextType | undefined>(undefined);

export const ReadOnlyProvider = ({ children }: { children: ReactNode }) => {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const { data: currentUser, isLoading: userLoading } = useAuthUser();
  const hasAccessToReadOnlyMode = useMemo(() => {
    if (userLoading || !currentUser?.email) return false;
    return configJson.readOnlyEmails.includes(currentUser.email);
  }, [currentUser, userLoading]);

  const setReadOnlyMode = (value: boolean) => {
    setIsReadOnly(value);
  };

  return (
    <ReadOnlyContext.Provider value={{ isReadOnly, setReadOnlyMode, hasAccessToReadOnlyMode }}>
      {children}
    </ReadOnlyContext.Provider>
  );
};

export const useReadOnlyContext = () => {
  const context = useContext(ReadOnlyContext);
  if (context === undefined) {
    throw new Error('useReadOnlyContext must be used within a ReadOnlyProvider');
  }
  return context;
};
